/**
 * QuadBatch — instanced, rotated, tinted atlas sprites in one draw call.
 *
 * Each instance has a world anchor plus an offset/size in "layout units". A per-instance scale
 * class chooses how layout units map to the world:
 *   0 = world units (terrain glyphs, particles — they zoom with the map)
 *   1 = uUnitA      (unit counters: screen size clamped to a readable range)
 *   2 = uUnitB      (markers & map symbols: gentler clamp)
 * so a counter, its bars, tags and heading tick scale together.
 */
import { Buffer, BufferUsage, Geometry, Mesh, type Shader, type Texture } from 'pixi.js';
import { packRgba } from '../palette';
import type { Frame } from './atlas';
import { destroyMesh, FRAG_HEADER, makeShader, VERT_HEADER } from './gl';

const FLOATS = 13;
const STRIDE = FLOATS * 4;

export const Scale = { World: 0, Counter: 1, Marker: 2 } as const;
export type ScaleClass = (typeof Scale)[keyof typeof Scale];

const VERT = /* glsl */ `${VERT_HEADER}
in vec2 aCorner;
in vec2 iPos;
in vec2 iOff;
in vec2 iSize;
in float iRot;
in float iScale;
in vec4 iUV;
in vec4 iColor;
uniform float uUnitA;
uniform float uUnitB;
out vec2 vUV;
out vec4 vColor;
void main() {
  float unit = iScale < 0.5 ? 1.0 : (iScale < 1.5 ? uUnitA : uUnitB);
  vec2 local = aCorner * iSize + iOff;
  float c = cos(iRot);
  float s = sin(iRot);
  local = vec2(c * local.x - s * local.y, s * local.x + c * local.y);
  vUV = mix(iUV.xy, iUV.zw, aCorner + 0.5);
  vColor = iColor;
  gl_Position = toClip(iPos + local * unit);
}
`;

const FRAG = /* glsl */ `${FRAG_HEADER}
in vec2 vUV;
in vec4 vColor;
uniform sampler2D uAtlas;
uniform vec4 uColor;
void main() {
  vec4 t = texture(uAtlas, vUV);
  float a = vColor.a * uColor.a;
  finalColor = t * vec4(vColor.rgb * uColor.rgb * a, a);
}
`;

export class QuadBatch {
  readonly mesh: Mesh<Geometry, Shader>;
  private readonly ibuf: Buffer;
  private readonly geometry: Geometry;
  private data: Float32Array;
  private u32: Uint32Array;
  private n = 0;
  private readonly shader: Shader;

  constructor(label: string, atlas: Texture, capacity = 256) {
    this.data = new Float32Array(capacity * FLOATS);
    this.u32 = new Uint32Array(this.data.buffer);
    const corners = new Buffer({
      data: new Float32Array([-0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5]),
      usage: BufferUsage.VERTEX,
    });
    const index = new Buffer({ data: new Uint32Array([0, 1, 2, 0, 2, 3]), usage: BufferUsage.INDEX });
    this.ibuf = new Buffer({ data: this.data, usage: BufferUsage.VERTEX | BufferUsage.COPY_DST, label });
    const at = (format: 'float32' | 'float32x2' | 'float32x4' | 'unorm8x4', offset: number) => ({
      buffer: this.ibuf,
      format,
      stride: STRIDE,
      offset,
      instance: true,
    });
    this.geometry = new Geometry({
      attributes: {
        aCorner: { buffer: corners, format: 'float32x2' },
        iPos: at('float32x2', 0),
        iOff: at('float32x2', 8),
        iSize: at('float32x2', 16),
        iRot: at('float32', 24),
        iScale: at('float32', 28),
        iUV: at('float32x4', 32),
        iColor: at('unorm8x4', 48),
      },
      indexBuffer: index,
      instanceCount: 0,
    });
    this.shader = makeShader('friction-quads', VERT, FRAG, {
      uAtlas: atlas.source,
      quadUniforms: { uUnitA: { value: 1, type: 'f32' }, uUnitB: { value: 1, type: 'f32' } },
    });
    this.mesh = new Mesh({ geometry: this.geometry, shader: this.shader });
    this.mesh.label = label;
    this.mesh.visible = false;
  }

  /** World units per layout unit for scale classes 1 and 2. */
  setUnits(unitA: number, unitB: number): void {
    const u = this.shader.resources.quadUniforms.uniforms;
    u.uUnitA = unitA;
    u.uUnitB = unitB;
  }

  get count(): number {
    return this.n;
  }

  clear(): void {
    this.n = 0;
  }

  push(
    x: number,
    y: number,
    offX: number,
    offY: number,
    w: number,
    h: number,
    frame: Frame,
    color: number,
    alpha: number,
    scale: ScaleClass = Scale.World,
    rot = 0,
  ): void {
    if ((this.n + 1) * FLOATS > this.data.length) {
      const next = new Float32Array(this.data.length * 2);
      next.set(this.data);
      this.data = next;
      this.u32 = new Uint32Array(next.buffer);
    }
    const o = this.n * FLOATS;
    const d = this.data;
    d[o] = x;
    d[o + 1] = y;
    d[o + 2] = offX;
    d[o + 3] = offY;
    d[o + 4] = w;
    d[o + 5] = h;
    d[o + 6] = rot;
    d[o + 7] = scale;
    d[o + 8] = frame.u0;
    d[o + 9] = frame.v0;
    d[o + 10] = frame.u1;
    d[o + 11] = frame.v1;
    this.u32[o + 12] = packRgba(color, alpha);
    this.n++;
  }

  commit(): void {
    if (this.ibuf.data !== this.data) this.ibuf.data = this.data;
    else this.ibuf.update(this.n * STRIDE);
    this.geometry.instanceCount = this.n;
    this.mesh.visible = this.n > 0;
  }

  destroy(): void {
    destroyMesh(this.mesh);
  }
}
